import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useOutreach } from '../../context/OutreachContext';
import { useLeadIntel } from '../../context/LeadIntelContext';
import { usePlaybook } from '../../context/PlaybookContext';
import SafeIcon from '../../common/SafeIcon';

// --- /go/:shortCode --- 
// ENDPOINT 3 Implementation: Redirect Service with Tracking & Advanced Logic
export const RedirectHandler = () => {
  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const { trackedLinks, logClickEvent } = useOutreach();
  const { ingestSignal } = useLeadIntel();
  const { runAiNextBestAction } = usePlaybook();

  const [errorPayload, setErrorPayload] = useState(null);
  const [status, setStatus] = useState('Initiating secure redirect...');

  useEffect(() => {
    const handleRedirect = async () => {
      // 1. Resolve Link
      const link = trackedLinks.find(l => l.short_code === code);
      if (!link) {
        setErrorPayload({ code: "LINK_NOT_FOUND", message: "Ugyldig eller utløpt lenke." });
        return;
      }

      // 2. Log Event (Standard Tracking)
      try {
        const userAgent = navigator.userAgent;
        const hashText = async (text) => {
          const msgBuffer = new TextEncoder().encode(text);
          const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        };
        const uaHash = await hashText(userAgent);
        const eventType = searchParams.get('ev') || 'Click';
        
        logClickEvent(eventType, link.id, link.microsite_id, uaHash);

        // 2b. Advanced Actions (Signal, Score, AI)
        if (link.on_click) {
          console.log("[Redirect] Executing advanced actions:", link.on_click);
          
          if (link.on_click.create_lead_signal) {
            // Ingest Signal to Lead Intel
            ingestSignal({
              source_id: 'link-tracking',
              signal_type: link.signal_type || 'Interest',
              priority_hint: link.priority_hint || 'Medium',
              title: `Clicked: ${link.short_code}`,
              summary: link.on_click.update_lead_score?.reason || 'User clicked tracked link',
              confidence: link.confidence || 1.0,
              occurred_at: new Date().toISOString(),
              // In real app, prospect/company ID would be resolved from link.prospect_id
              company_name: 'Unknown (Cookie)' 
            });
          }

          if (link.on_click.fire_ai_coach) {
            // Trigger AI Coach analysis
            runAiNextBestAction(link.campaign_id || 'general-context');
          }
        }

      } catch (e) {
        console.warn("Tracking failed (Fail-open triggered):", e);
      }

      // 3. Redirect
      let dest = link.destination_url;
      if (link.microsite_id && dest.includes('/ms/')) {
        const separator = dest.includes('?') ? '&' : '?';
        dest += `${separator}t=${link.id}`;
      }
      
      setStatus('Redirecting...');
      window.location.href = dest;
    };

    handleRedirect();
  }, [code, trackedLinks, searchParams, logClickEvent, ingestSignal, runAiNextBestAction]);

  if (errorPayload) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-50 font-mono text-sm p-8">
        <div className="bg-white p-8 rounded-2xl shadow-xl border border-rose-100 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon="⚠️" className="text-2xl text-rose-500" />
          </div>
          <h3 className="text-lg font-black text-slate-900 mb-2">404 Not Found</h3>
          <div className="bg-slate-900 text-slate-200 p-4 rounded-xl text-left text-xs font-mono overflow-auto">
            <pre>{JSON.stringify(errorPayload, null, 2)}</pre>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col items-center justify-center bg-slate-50 font-mono text-sm">
      <div className="w-12 h-12 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
      <p className="text-slate-500">{status}</p>
      <p className="text-xs text-slate-300 mt-2 font-bold uppercase tracking-widest">Sales Work Secure Link</p>
    </div>
  );
};

// --- /ms/:slug --- 
export const PublicMicrosite = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const { microsites, logClickEvent, handleLeadFormSubmit } = useOutreach();
  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const site = microsites.find(m => m.slug === slug);
  const trackingId = searchParams.get('t');

  useEffect(() => {
    if (site && trackingId) {
      logClickEvent('PageView', trackingId, site.id, 'ua-hash-placeholder');
    }
  }, [site, trackingId, logClickEvent]);

  if (!site) return <div className="h-screen flex items-center justify-center">Page not found</div>;

  const onSubmit = (e) => {
    e.preventDefault();
    handleLeadFormSubmit(site.id, formData);
    setSubmitted(true);
  };

  const handleCtaAction = (action) => {
    if (action === 'reply_email') {
      const subject = `Re: ${site.title}`;
      window.location.href = `mailto:?subject=${encodeURIComponent(subject)}`;
    }
  };

  const sections = Array.isArray(site.content?.sections) ? site.content.sections : [];

  return (
    <div className={`min-h-screen font-sans ${site.theme === 'dark' ? 'bg-slate-900 text-white' : 'bg-white text-slate-900'}`}>
      {sections.map((rawSec, idx) => {
        const sec = rawSec && typeof rawSec === 'object'
          ? rawSec
          : { type: 'unknown', title: 'Ukjent seksjon', text: '', items: [] };
        const type = String(sec?.type || 'content').toLowerCase();

        return (
          <section key={idx} className="py-16 px-4 md:px-8 border-b border-slate-100 last:border-0 border-opacity-10">
            <div className="max-w-4xl mx-auto">
              {type === 'hero' && (
                <div className="text-center space-y-6">
                  <h1 className="text-4xl md:text-6xl font-black tracking-tight">{sec.title}</h1>
                  <p className="text-xl opacity-80 max-w-2xl mx-auto">{sec.subtitle || sec.text}</p>
                  {sec.ctaAction && (
                    <div className="mt-8">
                      {sec.ctaAction === 'BookMeeting' ? (
                        <a 
                          href={sec.ctaTarget} 
                          target="_blank" 
                          rel="noreferrer"
                          onClick={() => logClickEvent('BookMeetingClick', trackingId, site.id, 'ua-hash')}
                          className="inline-block px-8 py-4 bg-blue-600 text-white rounded-full font-bold text-lg hover:bg-blue-700 transition-all shadow-lg"
                        >
                          {sec.ctaText}
                        </a>
                      ) : (
                        <button className="px-8 py-4 bg-slate-900 text-white rounded-full font-bold">{sec.ctaText}</button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {(type === 'problem' || type === 'content') && (
                <div className="grid md:grid-cols-1 gap-8 items-center max-w-2xl mx-auto text-center md:text-left">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold mb-4">{sec.title}</h2>
                    <p className="text-lg opacity-80 leading-relaxed">{sec.text}</p>
                  </div>
                </div>
              )}

              {type === 'list' && (
                <div className="max-w-2xl mx-auto">
                  <h2 className="text-2xl font-bold mb-6 text-center">{sec.title}</h2>
                  <ul className="space-y-3">
                    {Array.isArray(sec.items) && sec.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100">
                        <SafeIcon icon="✅" className="text-emerald-500 w-5 h-5 flex-shrink-0 mt-0.5" />
                        <span className="font-medium text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {type === 'cta' && (
                <div className="text-center bg-blue-50/50 p-10 rounded-[2.5rem] border border-blue-100">
                  <h2 className="text-3xl font-black text-blue-900 mb-4">{sec.title}</h2>
                  <p className="text-lg text-blue-800/80 mb-8 max-w-xl mx-auto">{sec.text}</p>
                  <button 
                    onClick={() => handleCtaAction(sec.ctaAction)}
                    className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 text-white rounded-full font-bold text-lg hover:bg-blue-700 transition-all shadow-xl shadow-blue-200"
                  >
                    {sec.ctaLabel || sec.ctaText || "Klikk her"} <SafeIcon icon="➡️" />
                  </button>
                </div>
              )}

              {type === 'leadform' && (
                <div className="max-w-md mx-auto bg-slate-50 p-8 rounded-3xl shadow-sm border border-slate-200">
                  {!submitted ? (
                    <form onSubmit={onSubmit} className="space-y-4">
                      <h3 className="text-xl font-bold text-center mb-6 text-slate-900">{sec.title}</h3>
                      <input required placeholder="Ditt navn" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full p-3 rounded-xl border border-slate-200 text-slate-900" />
                      <input required type="email" placeholder="E-post" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full p-3 rounded-xl border border-slate-200 text-slate-900" />
                      <input placeholder="Bedrift" value={formData.company} onChange={e => setFormData({...formData, company: e.target.value})} className="w-full p-3 rounded-xl border border-slate-200 text-slate-900" />
                      <button type="submit" className="w-full py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 shadow-lg">
                        Send forespørsel
                      </button>
                    </form>
                  ) : (
                    <div className="text-center py-10">
                      <SafeIcon icon="✅" className="text-6xl text-emerald-500 mb-4" />
                      <h3 className="text-xl font-bold text-slate-900">Takk!</h3>
                      <p className="text-slate-500">Vi kontakter deg snart.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        );
      })}
      <footer className="py-8 text-center text-sm opacity-50">
        Powered by Sales Work CRM
      </footer>
    </div>
  );
};